import { CheckCircleOutlined, ErrorOutlineOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { SvgIcon, Chip, ChipProps } from '@mui/material';
import { ExtendedListItem, ExtendedListItemText } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { SvbrgCheckAgainstPoliciesOutput, SvbrgComplianceStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';

export type IntakePoliciesCheckResultProps = {
  fileName: string;
  result: SvbrgCheckAgainstPoliciesOutput;
};

const propsByComplianceStatusMap: {
  [key in SvbrgComplianceStatus]: {
    color: ChipProps['color'];
    Icon: typeof SvgIcon;
    label: string;
  };
} = {
  [SvbrgComplianceStatus.compliant]: {
    color: 'success',
    Icon: CheckCircleOutlined,
    label: 'compliant',
  },
  [SvbrgComplianceStatus.risk]: {
    color: 'warning',
    Icon: WarningAmberOutlined,
    label: 'risk',
  },
  [SvbrgComplianceStatus.conflict]: {
    color: 'error',
    Icon: ErrorOutlineOutlined,
    label: 'conflict',
  },
};

// Component for just the nested parts without any wrapper
export const IntakePoliciesCheckResultParts: React.FC<IntakePoliciesCheckResultProps> = (props) => {
  const { t } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'IntakeQuoteComplianceStep.complianceResult',
  });

  const { result } = props;

  return (
    <>
      {result.parts.map((part, index) => {
        return (
          <ExtendedListItem key={index}>
            <ExtendedListItemText
              primary={part.sourceFileName || part.section || t('unknownSource')}
              secondary={part.details}
              sx={{ mr: 2 }}
            />
            <PolicyComplianceStatusChip status={part.status} />
          </ExtendedListItem>
        );
      })}
    </>
  );
};

export type PolicyComplianceStatusChipProps = {
  status: SvbrgComplianceStatus;
  clickable?: boolean;
  onClick?: () => void;
};

export const PolicyComplianceStatusChip: React.FC<PolicyComplianceStatusChipProps> = (props) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeCreate.compliance.statusChip' });

  const { status, clickable, onClick } = props;

  const { color, Icon, label } =
    propsByComplianceStatusMap[status] || propsByComplianceStatusMap[SvbrgComplianceStatus.compliant];

  return (
    <Chip
      label={t(label)}
      color={color}
      size="small"
      icon={<Icon />}
      sx={{ my: 0.5 }}
      clickable={clickable}
      onClick={onClick}
    />
  );
};
